<script setup lang="ts">
import { Bot, Check, Clipboard, ShieldCheck, TerminalSquare } from "lucide-vue-next"
import { ref } from "vue"

const installCommand = "curl -fsSL https://byre.cc/install.sh | sh"
const agentPrompt = "帮我安装 ByreCC，并接入我正在使用的 Agent。安装脚本在 https://byre.cc/install.sh。请完成设备登录和 MCP 配置，最后运行 byrectl doctor 确认安装成功。"
const copied = ref<"command" | "agent" | null>(null)
const installMode = ref<"terminal" | "agent">("terminal")

async function copy(value: string, target: "command" | "agent"): Promise<void> {
  await navigator.clipboard.writeText(value)
  copied.value = target
  window.setTimeout(() => {
    if (copied.value === target) copied.value = null
  }, 1600)
}
</script>

<template>
  <section class="connect-page" data-screen-label="接入 Agent">
    <div class="section-heading section-heading--page">
      <div class="page-heading">
        <span class="eyebrow">快速接入</span>
        <h1>接入 Agent</h1>
        <p>选择一种方式完成 ByreCC 安装与设备授权。</p>
      </div>
      <span class="install-platforms"><i></i>支持 macOS · Linux</span>
    </div>

    <div class="install-workbench">
      <nav class="install-methods" aria-label="安装方式">
        <button :class="{ active: installMode === 'terminal' }" @click="installMode = 'terminal'">
          <span class="install-method__number">01</span>
          <span class="install-method__icon"><TerminalSquare :size="18" /></span>
          <span><strong>复制一行命令</strong><small>熟悉终端时最快</small></span>
        </button>
        <button :class="{ active: installMode === 'agent' }" @click="installMode = 'agent'">
          <span class="install-method__number">02</span>
          <span class="install-method__icon"><Bot :size="18" /></span>
          <span><strong>直接告诉 Agent</strong><small>它会带你完成配置</small></span>
        </button>
        <div class="install-methods__note">
          <ShieldCheck :size="15" />
          <span>签名校验<br />设备授权</span>
        </div>
      </nav>

      <article class="install-stage" aria-live="polite">
        <template v-if="installMode === 'terminal'">
          <div class="install-stage__intro">
            <span class="eyebrow">终端安装</span>
            <h3>复制命令，完成接入</h3>
            <p>安装器会识别本机 Agent、验证安装包，并引导你完成设备授权。</p>
          </div>
          <div class="install-snippet">
            <header>
              <span class="install-snippet__meta"><span><i></i><i></i><i></i></span><code>install.sh</code></span>
              <button class="install-snippet__copy" @click="copy(installCommand, 'command')">
                <Check v-if="copied === 'command'" :size="13" />
                <Clipboard v-else :size="13" />
                {{ copied === "command" ? "已复制" : "复制" }}
              </button>
            </header>
            <div><code>{{ installCommand }}</code></div>
          </div>
        </template>

        <template v-else>
          <div class="install-stage__intro">
            <span class="eyebrow">Agent 安装</span>
            <h3>把这段话发给你的 Agent</h3>
            <p>适用于能够操作本机终端的 Agent；浏览器授权仍由你亲自确认。</p>
          </div>
          <div class="agent-prompt">
            <header>
              <code>prompt.txt</code>
              <button class="install-snippet__copy" @click="copy(agentPrompt, 'agent')">
                <Check v-if="copied === 'agent'" :size="13" />
                <Clipboard v-else :size="13" />
                {{ copied === "agent" ? "已复制" : "复制" }}
              </button>
            </header>
            <p>{{ agentPrompt }}</p>
          </div>
        </template>

        <footer>
          <span><Check :size="13" />无需 sudo</span>
          <span><Check :size="13" />API Key 不写入 Agent 配置</span>
          <span><Check :size="13" />可随时卸载</span>
        </footer>
      </article>
    </div>

    <footer class="install-followup">
      <span>已完成接入？</span>
      <div>
        <RouterLink to="/playground" class="inline-link">验证第一次调用 →</RouterLink>
        <RouterLink to="/quickstart" class="inline-link">手动配置 REST / MCP →</RouterLink>
      </div>
    </footer>
  </section>
</template>
